import React, { useEffect, useMemo, useState } from 'react'
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    TextField,
    Typography
} from '@mui/material'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'

const EXPIRING_WINDOW_MS = 24 * 60 * 60 * 1000

export function getSourceAccessState(review, now = Date.now()) {
    const access = review?.sourceAccess || {}
    const retentionEnabled = Boolean(access.retentionEnabled)
    const expiresAt = parseTime(access.expiresAt || review?.retention?.sourceExpiresAt)
    const deletedAt = parseTime(access.deletedAt)
    const expired = Boolean(expiresAt && expiresAt <= now)

    if (!retentionEnabled) {
        return {
            state: 'legacy',
            visible: false,
            canOpen: false,
            canManage: false,
            expiresAt: '',
            deletedAt: '',
            retainedFileIds: []
        }
    }

    const base = {
        visible: true,
        canAccess: Boolean(access.canAccess),
        owner: Boolean(access.owner),
        readOnly: Boolean(access.readOnly),
        reviewer: access.reviewer || null,
        expiresAt: expiresAt ? new Date(expiresAt).toISOString() : '',
        deletedAt: deletedAt ? new Date(deletedAt).toISOString() : '',
        retainedFileIds: Array.isArray(access.retainedFileIds) ? access.retainedFileIds.map(String) : []
    }

    if (deletedAt) {
        return { ...base, state: 'deleted', canOpen: false, canManage: false }
    }
    if (expired) {
        return { ...base, state: 'expired', canOpen: false, canManage: false }
    }
    if (!access.available) {
        if (!base.retainedFileIds.length && !Number(access.retainedFileCount || 0)) {
            return { ...base, state: 'empty', visible: false, canOpen: false, canManage: false }
        }
        return { ...base, state: 'unavailable', canOpen: false, canManage: false }
    }

    const expiring = Boolean(expiresAt && expiresAt - now <= EXPIRING_WINDOW_MS)
    return {
        ...base,
        state: expiring ? 'expiring' : 'active',
        canOpen: Boolean(access.canAccess),
        canManage: Boolean(access.owner)
    }
}

export function formatSourceAccessDate(value) {
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return ''
    return new Intl.DateTimeFormat(undefined, {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        timeZoneName: 'short'
    }).format(date)
}

export function sourceReviewerLabel(option) {
    if (!option) return ''
    return option.name || option.email || ''
}

export function sourceFileAccessErrorMessage(status, action = 'open') {
    if (Number(status) === 403) {
        return `You do not have access to ${action} this file. Ask the uploader to grant access.`
    }
    if (Number(status) === 410) {
        return 'This file is no longer available. The issue location is still available in the review.'
    }
    return `The source file could not be ${action === 'download' ? 'downloaded' : 'opened'}. Please try again.`
}

export function isSourceAccessPolicyResolved(status) {
    return status === 'enabled' || status === 'disabled'
}

export function isQualityReviewReadOnly(review) {
    return Boolean(review?.lifecycle?.readOnly || review?.sourceAccess?.readOnly)
}

export function resolveSourceReviewerOptionsClientId(selectedClient, review) {
    return String(selectedClient?._id || review?.clientId || '')
}

export function revokeObjectUrlAfterDownload(url, browserWindow = typeof window !== 'undefined' ? window : null, delayMs = 1000) {
    if (!url || !browserWindow?.URL?.revokeObjectURL || !browserWindow?.setTimeout) return
    browserWindow.setTimeout(() => browserWindow.URL.revokeObjectURL(url), delayMs)
}

export function SourceAccessPolicyNotice({ status, onRetry }) {
    if (status !== 'error') return null
    return (
        <Alert
            severity="error"
            action={(
                <Button color="inherit" size="small" onClick={onRetry}>
                    Retry
                </Button>
            )}
            sx={{ borderRadius: 2.5, mt: 1.5 }}
        >
            Source-file handling could not be confirmed. Retry before starting the quality check.
        </Alert>
    )
}

export function SourceReviewerSetup({
    enabled,
    reviewers = [],
    loading,
    value,
    onChange
}) {
    if (!enabled) return null

    return (
        <Box
            component="section"
            aria-labelledby="quality-review-source-access-setup-title"
            sx={{
                p: 2,
                borderRadius: 2.5,
                border: '1px solid #dbe6e8',
                backgroundColor: '#f8fbfa'
            }}
        >
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ md: 'center' }}>
                <Box sx={{ minWidth: 0 }}>
                    <Stack direction="row" spacing={0.75} alignItems="center">
                        <LockOutlinedIcon sx={{ color: '#2c675b', fontSize: 20 }} />
                        <Typography id="quality-review-source-access-setup-title" sx={{ fontWeight: 850, color: '#173f38' }}>
                            Source file access
                        </Typography>
                    </Stack>
                    <Typography variant="body2" sx={{ color: '#506f68', mt: 0.35 }}>
                        All uploaded files will be available for 14 days. You are always included; optionally allow one colleague too.
                    </Typography>
                </Box>
                {loading || reviewers.length > 0 ? (
                    <Autocomplete
                        size="small"
                        options={reviewers}
                        value={value || null}
                        loading={loading}
                        onChange={(_, option) => onChange?.(option)}
                        getOptionLabel={sourceReviewerLabel}
                        isOptionEqualToValue={(option, selected) => String(option.userId) === String(selected?.userId)}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Optional colleague"
                                placeholder="Only you"
                            />
                        )}
                        sx={{ width: { xs: '100%', md: 390 }, flexShrink: 0 }}
                    />
                ) : (
                    <Typography variant="body2" sx={{ color: '#60756f', fontWeight: 700 }}>
                        Files will be available only to you.
                    </Typography>
                )}
            </Stack>
        </Box>
    )
}

export function SourceAccessSummary({ review, now }) {
    const access = getSourceAccessState(review, now)
    if (!access.visible) return null

    const presentation = sourceAccessPresentation(access)
    return (
        <Stack
            component="p"
            direction="row"
            spacing={0.65}
            alignItems="flex-start"
            sx={{ m: 0, mt: 0.65, color: presentation.color, maxWidth: 820 }}
        >
            {access.state === 'expiring'
                ? <AccessTimeIcon sx={{ fontSize: 18, mt: '2px' }} />
                : <LockOutlinedIcon sx={{ fontSize: 18, mt: '2px' }} />}
            <Typography variant="body2" component="span" sx={{ color: 'inherit', fontWeight: access.state === 'expiring' ? 800 : 650 }}>
                {presentation.message}
            </Typography>
        </Stack>
    )
}

export function SavedReviewSourceStatus({ review, now }) {
    const access = getSourceAccessState(review, now)
    if (!access.visible) return null

    let message = ''
    if (access.state === 'active') message = `Source files available until ${formatSourceAccessDate(access.expiresAt)}`
    if (access.state === 'expiring') message = `Source files expire today at ${formatTimeOnly(access.expiresAt)}`
    if (access.state === 'deleted') message = 'Source files deleted'
    if (access.state === 'expired') message = 'Source files expired'
    if (access.state === 'unavailable') message = 'Source files no longer available'

    return message ? (
        <Typography variant="body2" sx={{ color: access.state === 'expiring' ? '#8a5a11' : '#627784', fontWeight: access.state === 'expiring' ? 800 : 650 }}>
            {message}
        </Typography>
    ) : null
}

export function ManageSourceAccessDialog({
    open,
    review,
    reviewers = [],
    loading,
    saving,
    onClose,
    onSave,
    onRevoke,
    onDeleteSources
}) {
    const access = getSourceAccessState(review)
    const currentReviewer = access.reviewer
    const availableReviewers = useMemo(() => mergeReviewerOptions(reviewers, currentReviewer), [reviewers, currentReviewer])
    const [selectedReviewer, setSelectedReviewer] = useState(currentReviewer)
    const [confirmingDelete, setConfirmingDelete] = useState(false)

    useEffect(() => {
        if (!open) return
        setSelectedReviewer(currentReviewer)
        setConfirmingDelete(false)
    }, [open, currentReviewer])

    const selectionChanged = String(selectedReviewer?.userId || '') !== String(currentReviewer?.userId || '')

    return (
        <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ fontWeight: 900, color: '#173042' }}>Source file access</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ pt: 0.5 }}>
                    <Typography variant="body2" color="text.secondary">
                        All uploaded files are available until {formatSourceAccessDate(access.expiresAt)}, then deleted automatically. Bubbly cannot remotely erase a file already opened or downloaded into a browser or device.
                    </Typography>
                    <Alert severity="info" sx={{ borderRadius: 2 }}>
                        You always have access as the uploader. You may allow one colleague.
                    </Alert>
                    <Autocomplete
                        size="small"
                        options={availableReviewers}
                        value={selectedReviewer || null}
                        loading={loading}
                        disabled={saving}
                        onChange={(_, option) => setSelectedReviewer(option)}
                        getOptionLabel={sourceReviewerLabel}
                        isOptionEqualToValue={(option, selected) => String(option.userId) === String(selected?.userId)}
                        renderInput={(params) => <TextField {...params} label="Optional colleague" placeholder="Only you" />}
                    />
                    {currentReviewer && (
                        <Button variant="outlined" color="warning" disabled={saving} onClick={onRevoke} sx={{ alignSelf: 'flex-start' }}>
                            Revoke colleague access
                        </Button>
                    )}
                    <Box sx={{ pt: 1.5, borderTop: '1px solid #e4eaee' }}>
                        {!confirmingDelete ? (
                            <Button
                                color="error"
                                variant="text"
                                startIcon={<DeleteOutlineIcon />}
                                disabled={saving}
                                onClick={() => setConfirmingDelete(true)}
                            >
                                Delete source files now
                            </Button>
                        ) : (
                            <Alert severity="warning" sx={{ borderRadius: 2 }}>
                                <Stack spacing={1}>
                                    <Typography variant="body2">
                                        Delete all retained source files now? Results and page references will remain. This cannot be undone.
                                    </Typography>
                                    <Stack direction="row" spacing={1}>
                                        <Button size="small" onClick={() => setConfirmingDelete(false)} disabled={saving}>Cancel</Button>
                                        <Button size="small" color="error" variant="contained" onClick={onDeleteSources} disabled={saving}>
                                            Delete files
                                        </Button>
                                    </Stack>
                                </Stack>
                            </Alert>
                        )}
                    </Box>
                </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5 }}>
                <Button onClick={onClose} disabled={saving}>Close</Button>
                <Button
                    variant="contained"
                    disabled={saving || !selectedReviewer || !selectionChanged}
                    onClick={() => onSave?.(selectedReviewer.userId)}
                >
                    {saving ? 'Saving…' : 'Save access'}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

function sourceAccessPresentation(access) {
    if (access.state === 'deleted') {
        return {
            color: '#647783',
            message: `Source files were deleted on ${formatSourceAccessDate(access.deletedAt)}. Page references remain.`
        }
    }
    if (access.state === 'expired') {
        return {
            color: '#647783',
            message: `Issue file access expired on ${formatSourceAccessDate(access.expiresAt)}. Page references remain.`
        }
    }
    if (access.state === 'unavailable') {
        return { color: '#647783', message: 'Source files are no longer available. Page references remain.' }
    }

    const accessText = access.owner
        ? access.reviewer?.name
            ? `Accessible to you and ${access.reviewer.name}.`
            : 'Accessible only to you.'
        : access.readOnly
            ? 'You have read-only access.'
            : 'Restricted to the uploader and selected reviewer.'
    if (access.state === 'expiring') {
        return {
            color: '#8a5a11',
            message: `Source files expire today at ${formatTimeOnly(access.expiresAt)}. ${accessText}`
        }
    }
    return {
        color: '#526b7b',
        message: `Source files available until ${formatSourceAccessDate(access.expiresAt)}. ${accessText}`
    }
}

function mergeReviewerOptions(reviewers, currentReviewer) {
    const output = [...(reviewers || [])]
    if (currentReviewer && !output.some(option => String(option.userId) === String(currentReviewer.userId))) {
        output.unshift(currentReviewer)
    }
    return output
}

function formatTimeOnly(value) {
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return ''
    return new Intl.DateTimeFormat(undefined, {
        hour: 'numeric',
        minute: '2-digit',
        timeZoneName: 'short'
    }).format(date)
}

function parseTime(value) {
    const time = new Date(value || '').getTime()
    return Number.isFinite(time) ? time : 0
}
